import React, { KeyboardEvent } from 'react';

import styled from 'styled-components';

import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import UIButton, { EUIButtonVariant } from '@aquariuxfintech/aqx-trader-react-ui/button';
import UIFormItem, { EUIFormItemVariant } from '@aquariuxfintech/aqx-trader-react-ui/form';
import { ESpacing, ETypography } from '@aquariuxfintech/aqx-trader-react-ui/foundation';
import UIInput, { EUIInputVariant } from '@aquariuxfintech/aqx-trader-react-ui/input';
import UIText from '@aquariuxfintech/aqx-trader-react-ui/text';

import { EPasswordCriteriaType, PASSWORD_LENGTH } from '@aqx-core/common/constants/password';
import { EDataTestId } from '@aqx-core/common/data-test-id/types';
import { useSignUpCrmForm } from '@aqx-core/common/hooks/form/use-sign-up-crm-form';
import { useSignupCrm } from '@aqx-core/common/services/sign-up-crm';
import { getEnterKeycode } from '@aqx-core/common/utils/keyboard';

import AuthPasswordField from '../../../shared/password';
import SignUpCrmAcknowledgementField from './acknowledgement';
import SignUpCrmBasicFormAdditionalFields from './additional-info';

const { min: minPasswordLength, max: maxPasswordLength } = PASSWORD_LENGTH[EPasswordCriteriaType.LIRUNEX_CRM];

const SignUpCrmBasicForm: FCC = () => {
  const { t } = useTranslation();
  const { loading } = useSignupCrm();
  const { useFormMethods, submit, isDisabled, email, password, customerType, countryId, tncAcknowledged } =
    useSignUpCrmForm();
  const { formState, register, setValue, trigger } = useFormMethods;

  const {
    errors: {
      email: emailError,
      password: passwordError,
      customerType: customerTypeError,
      countryId: countryIdError,
      tncAcknowledged: tncAcknowledgedError
    }
  } = formState;

  const handleKeyDown = (event: KeyboardEvent) => {
    if (getEnterKeycode(event)) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <FormProvider {...useFormMethods}>
      <StyledFormContainer onSubmit={submit}>
        <UIText typography={ETypography.DISPLAY_M_BOLD}>{t('Sign up')}</UIText>

        <StyledFormInputsContainer>
          <UIFormItem variant={EUIFormItemVariant.ITEM_WITH_FIELD_LABEL} label={'Email'} size="large">
            <UIInput
              {...register('email')}
              variant={EUIInputVariant.BORDERED_TEXT}
              placeholder={t('Enter your email')}
              value={email}
              error={emailError?.message}
              onKeyDown={handleKeyDown}
              dataTestId={EDataTestId.SIGN_UP_EMAIL_INPUT}
              size="large"
            />
          </UIFormItem>

          <AuthPasswordField
            label={t('Password')}
            password={password}
            error={passwordError?.message}
            register={register}
            handleKeyDown={handleKeyDown}
            minLength={minPasswordLength}
            maxLength={maxPasswordLength}
          />

          <SignUpCrmBasicFormAdditionalFields
            setValue={setValue}
            customerType={customerType}
            customerTypeError={customerTypeError?.message}
            countryId={countryId}
            countryIdError={countryIdError?.message}
            trigger={trigger}
          />

          <SignUpCrmAcknowledgementField
            tncAcknowledged={tncAcknowledged}
            error={tncAcknowledgedError?.message}
            trigger={trigger}
            setValue={setValue}
          />
        </StyledFormInputsContainer>

        <StyledButtonContainer>
          <UIButton
            variant={EUIButtonVariant.FILLED_THEMED}
            stretch={true}
            disabled={isDisabled || loading}
            dataTestId={EDataTestId.SIGN_UP_BASIC_CONTINUE}
            size="large"
          >
            {t('Next')}
          </UIButton>
        </StyledButtonContainer>
      </StyledFormContainer>
    </FormProvider>
  );
};

export default SignUpCrmBasicForm;

const StyledFormContainer = styled.form`
  display: flex;
  flex-direction: column;
`;

const StyledFormInputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${ESpacing.XL}px;
  margin-top: ${ESpacing.XXXL}px;
  margin-bottom: ${ESpacing.XL}px;
`;

const StyledButtonContainer = styled.div`
  display: flex;
`;
